import { useState, type FC, type FormEvent } from "react";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";

export const LoginPage: FC = () => {
  const { login } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!username.trim() || !password) {
      setError("Please enter both username and password.");
      return;
    }

    setLoading(true);
    // login() now covers both staff (sync) and catalyst (network) flows —
    // the artificial delay is gone since catalyst login already has real latency.
    try {
      const ok = await login(username, password);
      if (!ok) setError("Invalid username or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-navy flex items-center justify-center p-4">
      {/* Background grid texture */}
      <div
        className="fixed inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="w-full max-w-md relative">
        {/* Logo / brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16
                          bg-brand-orange rounded-2xl mb-4 shadow-lg
                          shadow-brand-orange/30">
            <span className="text-white font-bold text-2xl">G</span>
          </div>
          <h1 className="text-white text-2xl font-bold">SW Syria 2026</h1>
          <p className="text-brand-muted text-sm mt-1">Applicants Dashboard</p>
        </div>

        {/* Card */}
        <div className="card p-8 shadow-2xl shadow-black/40">
          <h2 className="text-white font-semibold text-lg mb-6">Sign in</h2>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* Username */}
            <div>
              <label className="block text-xs font-medium text-brand-muted mb-1.5">
                Username or email
              </label>
              <input
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="damascus, or you@example.com"
                className="w-full bg-brand-navy border border-brand-border text-white
                           rounded-lg px-4 py-2.5 text-sm placeholder:text-brand-muted/50
                           focus:outline-none focus:border-brand-orange transition-colors"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-medium text-brand-muted mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-brand-navy border border-brand-border text-white
                             rounded-lg px-4 py-2.5 pr-10 text-sm
                             placeholder:text-brand-muted/50
                             focus:outline-none focus:border-brand-orange transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2
                             text-brand-muted hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/20
                            rounded-lg px-4 py-2.5">
                {error}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2
                         py-2.5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white
                                 rounded-full animate-spin" />
              ) : (
                <LogIn size={16} />
              )}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>

        <p className="text-center text-brand-muted/50 text-xs mt-6">
          SW Syria 2026 · Internal Dashboard
        </p>
      </div>
    </div>
  );
};